import { useEffect, useState } from 'react';
import { Box, Typography, Button, ToggleButtonGroup, ToggleButton, CircularProgress, Alert } from '@mui/material';
import { unitLabel as getUnitLabel } from '../../utils/testUnits';

// TEAM TESTING LEADERBOARDS — first phase (SESSION + SEASON). Pure
// companion panel to TeamTestingStation: reuses the Station's OWN
// currently-selected test (`testType` prop) — deliberately no second,
// independent test selector anywhere in this component. SEASON is the
// default scope, per product decision.
//
// Router-free, props/callbacks only (fetchSessionLeaderboard/
// fetchSeasonLeaderboard are injected, same discipline as
// TeamTestingStation's own fetchAthleteSessions/recordAttempt), so this
// stays directly unit-testable with RTL.
//
// `refreshToken` is the smallest clean invalidation mechanism that fits
// the current component structure: the caller (TeamTestingStation)
// bumps it ONLY after a successful RECORD — never on STOP/RETRY/Cancel,
// which never touch it at all — and this component's own effect simply
// refetches whenever testType/scope/refreshToken change. No pub/sub, no
// global cache, no extra state machine.
export default function TeamTestLeaderboard({ testType, testDate, fetchSessionLeaderboard, fetchSeasonLeaderboard, refreshToken }) {
  const [scope, setScope] = useState('season'); // 'session' | 'season' — SEASON is the default view
  const [expanded, setExpanded] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!testType || !expanded) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // 054 PRE-APPLICATION REVIEW — SESSION explicitly passes the SAME
    // testDate the Station just wrote attempts under; never relies on
    // the RPC's own `current_date` SQL default (which could evaluate in
    // a different timezone than the client's computed date). SEASON has
    // no date concept at all and ignores it entirely.
    const fetchPromise = scope === 'session'
      ? fetchSessionLeaderboard(testType.key, testDate)
      : fetchSeasonLeaderboard(testType.key);
    fetchPromise
      .then(data => { if (!cancelled) setRows(data); })
      .catch(e => { if (!cancelled) setError(e.message || 'Could not load the leaderboard.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [testType, testDate, scope, expanded, refreshToken, fetchSessionLeaderboard, fetchSeasonLeaderboard]);

  if (!testType) return null;
  // BROWSER ACCEPTANCE BUG FIX (056 follow-up) — this used to be a local
  // `testType.unit === 'seconds' ? 'sec' : 'in'` binary, left over from
  // before Custom Test Types existed. It silently mislabeled every
  // non-seconds unit as "in" (a Program's own reps-based "Push-Ups" test
  // rendered "31 in" here even though the capture UI elsewhere already
  // used the correct "reps" label via the same shared utility). test_types
  // metadata is the source of truth for a unit's display label, and
  // testUnits.js is the one shared formatter for it — reused here instead
  // of a second, independent, and now-incorrect binary.
  const unitLabel = getUnitLabel(testType.unit);

  return (
    <Box sx={{ mt: 3, border: '1px solid #1e2a3a', borderRadius: 2, backgroundColor: 'rgba(10,20,34,0.6)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', letterSpacing: '0.04em', color: '#9eb8d0' }}>
          {testType.display_name.toUpperCase()} LEADERBOARD
        </Typography>
        <Button
          size="small" onClick={() => setExpanded(e => !e)}
          sx={{ textTransform: 'none', color: '#6a8aa8', minWidth: 0 }}
        >
          {expanded ? 'Hide' : 'Show'}
        </Button>
      </Box>

      {expanded && (
        <Box sx={{ px: 2, pb: 2 }}>
          <ToggleButtonGroup
            value={scope}
            exclusive
            onChange={(e, v) => v && setScope(v)}
            size="small"
            sx={{ mb: 1.5, '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 2 } }}
          >
            <ToggleButton value="session">Session</ToggleButton>
            <ToggleButton value="season">Season</ToggleButton>
          </ToggleButtonGroup>

          {loading ? (
            <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} sx={{ color: '#2ea84b' }} /></Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : rows.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>No eligible results yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {rows.map(row => (
                <Box key={row.athlete_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                  <Typography sx={{ fontWeight: 800, width: 28, color: '#6a8aa8' }}>#{row.leaderboard_rank}</Typography>
                  <Typography sx={{ flex: 1, fontWeight: 700 }}>
                    {row.jersey_number !== null && row.jersey_number !== undefined && `#${row.jersey_number} `}
                    {row.first_name} {row.last_name}
                  </Typography>
                  <Typography sx={{ fontWeight: 800, color: '#2ea84b' }}>{row.result_value} {unitLabel}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
